import React, {useCallback, useEffect, useMemo, useState} from 'react'
import {
	Alert,
	Box,
	Button,
	Chip,
	CircularProgress,
	Container,
	Divider,
	IconButton,
	MenuItem,
	Paper,
	Stack,
	TextField,
	ToggleButton,
	ToggleButtonGroup,
	Tooltip,
	Typography,
	useTheme,
}                                 from '@mui/material'
import Grid                       from '@mui/material/Unstable_Grid2'
import {
	ArrowBack,
	Close,
	ErrorOutline,
	InfoOutlined,
	Refresh,
	WarningAmber,
}                                 from '@mui/icons-material'
import {DateTimePicker}           from '@mui/x-date-pickers'
import {useNavigate}              from 'react-router-dom'
import dayjs                      from 'dayjs'
import MDEditor                   from '@uiw/react-md-editor'
import ReactMarkdown              from 'react-markdown'
import {doGet, doPut}             from '../../util/do-fetch'

/*
 * The announcement editor (190).
 *
 * There is exactly ONE announcement, so this is a single form rather than a list: no table, no
 * "new", no delete. Writing a new announcement replaces the last one, and its history is the
 * config history of the ANNOUNCEMENT key (App Config -> history), because that is where it is
 * stored.
 *
 * WHO SEES IT is not a field on this screen, deliberately. Only users who signed up BEFORE the
 * publish date are shown it, which is what makes an announcement reach existing users and not new
 * ones — see service/announcement.js for the rule. The one control that decides the audience is
 * therefore the publish date, which is why it is given its own section with the reach figures
 * beside it.
 */

// mirrors service/announcement.js. The server validates and is authoritative; these exist so the
// form can say what is wrong before a save round-trip.
const MAX_TITLE = 120
const MAX_BODY  = 4000

const SEVERITIES = [
	{value: 'info',     label: 'Info',     Icon: InfoOutlined},
	{value: 'warning',  label: 'Warning',  Icon: WarningAmber},
	{value: 'critical', label: 'Critical', Icon: ErrorOutline},
]

const EMPTY = {
	title:      '',
	body:       '',
	severity:   'info',
	placement:  'modal',
	publish_at: null,
	expires_at: null,
}

const STATUS_META = {
	draft: {
		label: 'Draft',
		color: 'default',
		help:  'Nobody sees this. Set a publish date to send it.',
	},
	scheduled: {
		label: 'Scheduled',
		color: 'info',
		help:  'Nobody sees this yet. Anyone who signs up before the publish date will still be shown it when it goes live.',
	},
	live: {
		label: 'Live',
		color: 'success',
		help:  'Being shown to users who signed up before the publish date and have not closed it yet.',
	},
	expired: {
		label: 'Expired',
		color: 'warning',
		help:  'Past its expiry date, so nobody is shown it any more.',
	},
}

/*
 * Repeated from service/announcement.js so the status chip and the preview react to an UNSAVED
 * edit — the server cannot tell you the status of something it has not been given yet. The server
 * still decides what users actually see.
 */
const statusOf = (announcement) => {
	if (!announcement.publish_at) return 'draft'
	const now = Date.now()
	if (new Date(announcement.publish_at).getTime() > now) return 'scheduled'
	if (announcement.expires_at && new Date(announcement.expires_at).getTime() <= now) return 'expired'
	return 'live'
}

// pre-flight only; the server's message wins if it disagrees
const problemWith = (announcement) => {
	if (announcement.publish_at) {
		if (!announcement.title.trim()) return 'A title is required before publishing.'
		if (!announcement.body.trim())  return 'A message is required before publishing.'
	}
	if (announcement.expires_at && !announcement.publish_at) {
		return 'An expiry date needs a publish date.'
	}
	if (announcement.publish_at && announcement.expires_at
		&& new Date(announcement.expires_at) <= new Date(announcement.publish_at)) {
		return 'Expiry must be after the publish date.'
	}
	return null
}

const severityColor = (severity, custom) => {
	if (severity === 'critical') return custom.status.danger
	if (severity === 'warning')  return custom.status.amber
	return custom.link
}

const toDayjs = (iso) => (iso ? dayjs(iso) : null)
const toIso = (value) => (value && value.isValid && value.isValid() ? value.toISOString() : null)

/* =====================
   PREVIEW
   ===================== */

/*
 * What the user actually sees. This is load-bearing rather than decoration: there is no way to
 * target yourself for a test, so without a preview the first time anyone sees an announcement
 * rendered is after it has gone out to everybody.
 */
const AnnouncementPreview = ({announcement}) => {
	const theme = useTheme()
	const custom = theme.custom
	const accent = severityColor(announcement.severity, custom)
	const {Icon} = SEVERITIES.find(s => s.value === announcement.severity) || SEVERITIES[0]

	const title = announcement.title.trim() || 'Untitled announcement'
	const body = announcement.body.trim()

	if (announcement.placement === 'banner') {
		return (
			<Box sx={{
				display: 'flex',
				alignItems: 'flex-start',
				gap: 1.5,
				p: 1.5,
				borderRadius: 2,
				border: `1px solid ${accent}`,
				borderLeft: `4px solid ${accent}`,
				background: theme.palette.background.paper,
			}}>
				<Icon sx={{color: accent, fontSize: 20, mt: '2px'}}/>
				<Box sx={{flex: 1, minWidth: 0}}>
					<Typography sx={{fontWeight: custom.fontWeight.bold}}>{title}</Typography>
					{body && (
						<Box sx={{'& p': {m: 0}, fontSize: custom.fontSize.sm, color: 'text.secondary'}}>
							<ReactMarkdown>{body}</ReactMarkdown>
						</Box>
					)}
				</Box>
				<Close sx={{fontSize: 18, color: 'text.disabled'}}/>
			</Box>
		)
	}

	// modal
	return (
		<Paper elevation={8} sx={{
			p: 3,
			borderRadius: 3,
			borderTop: `4px solid ${accent}`,
			maxWidth: 440,
			mx: 'auto',
		}}>
			<Box sx={{display: 'flex', alignItems: 'center', gap: 1, mb: 1.5}}>
				<Icon sx={{color: accent}}/>
				<Typography variant="h6" sx={{fontWeight: custom.fontWeight.bold}}>{title}</Typography>
			</Box>
			<Box sx={{color: 'text.secondary', '& p:first-of-type': {mt: 0}, '& p:last-of-type': {mb: 0}}}>
				{body
					? <ReactMarkdown>{body}</ReactMarkdown>
					: <Typography color="text.disabled">No message yet.</Typography>}
			</Box>
			<Box sx={{display: 'flex', justifyContent: 'flex-end', mt: 3}}>
				<Button variant="contained" disableElevation>Got it</Button>
			</Box>
		</Paper>
	)
}

/* =====================
   REACH
   ===================== */

/*
 * Eligibility is counted from the signup date, not from the seen timestamp — a user who signed up
 * after the publish date and a user who has acknowledged it both sit after publish_at, so the seen
 * column alone cannot tell them apart. See fetchAnnouncementReach in service/announcement.js.
 */
const Reach = ({reach, status}) => {
	/*
	 * A null `reach` means one of TWO things, and they must not look the same (201).
	 *
	 * fetchAnnouncementReach returns null for a draft AND null when its count query fails. Showing
	 * the draft sentence for both made a live, published announcement report itself as a draft — and
	 * the actual fault (user_profile.announcement_seen_at missing, so the query 400s) was invisible
	 * from this screen. `status` already distinguishes them, so it is used rather than guessed at.
	 */
	if (!reach) {
		return status === 'draft'
			? (
				<Typography variant="body2" color="text.secondary">
					A draft has no publish date, so there is nobody to count yet.
				</Typography>
			)
			: (
				<Alert severity="warning" sx={{mt: 1}}>
					This announcement is <strong>{status}</strong>, but its reach could not be counted.
					That usually means <code>user_profile.announcement_seen_at</code> is missing — apply
					<code> schema-changes/017-announcement-seen-at.sql</code>. Until then the announcement
					cannot be shown to users either, because the same column decides who has seen it.
				</Alert>
			)
	}

	const figures = [
		{label: 'Existing users it applies to', value: reach.eligible},
		{label: 'Have seen it',                 value: reach.seen},
		{label: 'Still to see it',              value: reach.pending},
	]

	return (
		<>
			<Stack divider={<Divider flexItem/>} spacing={1}>
				{figures.map(({label, value}) => (
					<Box key={label} sx={{display: 'flex', justifyContent: 'space-between', gap: 2}}>
						<Typography variant="body2" color="text.secondary">{label}</Typography>
						<Typography variant="body2" sx={{fontWeight: 700}}>{value.toLocaleString()}</Typography>
					</Box>
				))}
			</Stack>
			{status === 'scheduled' && (
				<Typography variant="caption" color="text.secondary" sx={{display: 'block', mt: 1.5}}>
					Counted against the publish date, so this figure will grow as people sign up before it.
				</Typography>
			)}
		</>
	)
}

/* =====================
   SCREEN
   ===================== */

const Announcements = () => {
	const navigate = useNavigate()
	const theme = useTheme()

	const [draft, setDraft]     = useState(null)
	const [server, setServer]   = useState(null)   // {announcement, status, reach} as last saved
	const [loading, setLoading] = useState(true)
	const [saving, setSaving]   = useState(false)
	const [error, setError]     = useState(null)
	const [saved, setSaved]     = useState(false)

	const load = useCallback(async () => {
		try {
			const response = await doGet('/admin/announcement')
			if (!response.ok) throw new Error(await response.text())
			const data = await response.json()
			setServer(data)
			setDraft({...EMPTY, ...data.announcement})
			setError(null)
		}
		catch (err) {
			console.error(err)
			setError('Could not load the announcement.')
		}
		finally {
			setLoading(false)
		}
	}, [])

	useEffect(() => { load() }, [load])

	const setField = (field) => (value) => {
		setSaved(false)
		setDraft(prev => ({...prev, [field]: value}))
	}

	const save = async () => {
		setSaving(true)
		setError(null)
		try {
			const response = await doPut('/admin/announcement', {body: JSON.stringify(draft)})
			if (!response.ok) throw new Error(await response.text())
			const data = await response.json()
			setServer(data)
			setDraft({...EMPTY, ...data.announcement})
			setSaved(true)
		}
		catch (err) {
			console.error(err)
			setError(err.message || 'Could not save the announcement.')
		}
		finally {
			setSaving(false)
		}
	}

	const dirty = useMemo(
		() => Boolean(draft && server) && JSON.stringify(draft) !== JSON.stringify({...EMPTY, ...server.announcement}),
		[draft, server]
	)

	if (loading) {
		return (
			<Container maxWidth="lg" sx={{py: 6, display: 'flex', justifyContent: 'center'}}>
				<CircularProgress/>
			</Container>
		)
	}

	if (!draft) {
		return (
			<Container maxWidth="lg" sx={{py: 4}}>
				<Alert severity="error">{error || 'Could not load the announcement.'}</Alert>
			</Container>
		)
	}

	const status = statusOf(draft)
	const meta = STATUS_META[status]
	const problem = problemWith(draft)

	// the status the announcement is actually in right now, which is what the reach figures describe
	const liveStatus = server ? server.status : status

	return (
		<Container maxWidth="lg" sx={{py: 4}}>

			<Box sx={{display: 'flex', alignItems: 'center', gap: 1, mb: 1}}>
				<IconButton onClick={() => navigate('/admin')} size="small"><ArrowBack/></IconButton>
				<Typography variant="h5" sx={{fontWeight: 700, flex: 1}}>Announcement</Typography>
				<Chip size="small" label={meta.label} color={meta.color}/>
				{dirty && <Chip size="small" variant="outlined" label="Unsaved changes"/>}
				<Tooltip title="Reload">
					<IconButton onClick={load} size="small"><Refresh/></IconButton>
				</Tooltip>
			</Box>

			<Typography variant="body2" color="text.secondary" sx={{mb: 3}}>
				One message, shown once to users who were already here — to announce an issue, a warning,
				or a change. Saving a new one replaces the last. Users who sign up after the publish date
				are never shown it.
			</Typography>

			{error && <Alert severity="error" sx={{mb: 2}} onClose={() => setError(null)}>{error}</Alert>}
			{saved && !dirty && <Alert severity="success" sx={{mb: 2}} onClose={() => setSaved(false)}>Saved.</Alert>}

			{/*
			  * The one genuinely surprising rule, so it is stated on the screen rather than left to be
			  * discovered: an edit does not go back to anybody who has already closed it.
			  */}
			{liveStatus === 'live' && (
				<Alert severity="info" sx={{mb: 2}}>
					This announcement is live. Editing the wording does <strong>not</strong> show it again to
					anyone who has already closed it — move the publish date forward to do that.
				</Alert>
			)}

			<Grid container spacing={3}>

				{/* ---------- editor ---------- */}
				<Grid xs={12} md={7}>
					<Paper variant="outlined" sx={{p: 3, borderRadius: 3}}>

						<TextField
							label="Title"
							fullWidth
							value={draft.title}
							onChange={(e) => setField('title')(e.target.value.slice(0, MAX_TITLE))}
							helperText={`${draft.title.length}/${MAX_TITLE}`}
							sx={{mb: 3}}
						/>

						<Typography variant="subtitle2" sx={{mb: 1}}>Message</Typography>
						{/* data-color-mode is how @uiw/react-md-editor follows a dark theme */}
						<Box data-color-mode={theme.palette.mode} sx={{mb: 1}}>
							<MDEditor
								value={draft.body}
								height={280}
								onChange={(value) => setField('body')((value || '').slice(0, MAX_BODY))}
							/>
						</Box>
						<Typography variant="caption" color="text.secondary">
							Markdown. {draft.body.length}/{MAX_BODY}
						</Typography>

						<Divider sx={{my: 3}}/>

						<Grid container spacing={2}>
							<Grid xs={12} sm={6}>
								<TextField
									select
									label="Severity"
									fullWidth
									value={draft.severity}
									onChange={(e) => setField('severity')(e.target.value)}
									helperText="Colour and icon only."
								>
									{SEVERITIES.map(({value, label}) => (
										<MenuItem key={value} value={value}>{label}</MenuItem>
									))}
								</TextField>
							</Grid>
							<Grid xs={12} sm={6}>
								<Typography variant="body2" color="text.secondary" sx={{mb: 0.5}}>Show as</Typography>
								<ToggleButtonGroup
									exclusive
									size="small"
									value={draft.placement}
									onChange={(e, value) => value && setField('placement')(value)}
								>
									<ToggleButton value="modal">Modal</ToggleButton>
									<ToggleButton value="banner">Banner</ToggleButton>
								</ToggleButtonGroup>
								<Typography variant="caption" color="text.secondary" sx={{display: 'block', mt: 0.5}}>
									A modal interrupts; a banner sits above the page.
								</Typography>
							</Grid>
						</Grid>

						<Divider sx={{my: 3}}/>

						{/*
						  * The publish date is the audience control, not just a schedule — everyone who
						  * signed up before it is shown the announcement, and nobody who signed up after
						  * it ever is. Hence the prominence and the explanatory caption.
						  */}
						<Typography variant="subtitle2">When</Typography>
						<Typography variant="caption" color="text.secondary" sx={{display: 'block', mb: 2}}>
							The publish date decides who sees this: users who signed up before it, and nobody
							who signed up after. Leave it empty to keep this as a draft.
						</Typography>

						<Grid container spacing={2}>
							<Grid xs={12} sm={6}>
								<DateTimePicker
									label="Publish"
									value={toDayjs(draft.publish_at)}
									onChange={(value) => setField('publish_at')(toIso(value))}
									slotProps={{
										textField: {fullWidth: true},
										field: {clearable: true, onClear: () => setField('publish_at')(null)},
									}}
								/>
								<Stack direction="row" spacing={1} sx={{mt: 1}}>
									<Button size="small" onClick={() => setField('publish_at')(new Date().toISOString())}>
										Now
									</Button>
									{draft.publish_at && (
										<Button size="small" color="inherit" onClick={() => {
											setField('publish_at')(null)
											setDraft(prev => ({...prev, publish_at: null, expires_at: null}))
										}}>
											Make draft
										</Button>
									)}
								</Stack>
							</Grid>
							<Grid xs={12} sm={6}>
								<DateTimePicker
									label="Expires (optional)"
									value={toDayjs(draft.expires_at)}
									minDateTime={toDayjs(draft.publish_at) || undefined}
									onChange={(value) => setField('expires_at')(toIso(value))}
									slotProps={{
										textField: {fullWidth: true},
										field: {clearable: true, onClear: () => setField('expires_at')(null)},
									}}
								/>
								<Typography variant="caption" color="text.secondary" sx={{display: 'block', mt: 1}}>
									Leave empty to show it until it is replaced. Worth setting for anything
									time-bound, so a returning user is not told about maintenance that has passed.
								</Typography>
							</Grid>
						</Grid>

						<Divider sx={{my: 3}}/>

						<Box sx={{display: 'flex', alignItems: 'center', gap: 2}}>
							<Button
								variant="contained"
								disableElevation
								onClick={save}
								disabled={saving || !dirty || Boolean(problem)}
							>
								{saving ? 'Saving…' : 'Save'}
							</Button>
							{problem && <Typography variant="body2" color="error">{problem}</Typography>}
							{!problem && !dirty && (
								<Typography variant="body2" color="text.secondary">No changes to save.</Typography>
							)}
						</Box>

					</Paper>
				</Grid>

				{/* ---------- preview + reach ---------- */}
				<Grid xs={12} md={5}>

					<Paper variant="outlined" sx={{p: 2.5, borderRadius: 3, mb: 3}}>
						<Typography variant="subtitle2" sx={{mb: 0.5}}>Preview</Typography>
						<Typography variant="caption" color="text.secondary" sx={{display: 'block', mb: 2}}>
							{meta.help}
						</Typography>
						<Box sx={{
							p: 2,
							borderRadius: 2,
							background: theme.palette.action.hover,
						}}>
							<AnnouncementPreview announcement={draft}/>
						</Box>
					</Paper>

					<Paper variant="outlined" sx={{p: 2.5, borderRadius: 3}}>
						<Box sx={{display: 'flex', alignItems: 'center', mb: 2}}>
							<Typography variant="subtitle2" sx={{flex: 1}}>Reach</Typography>
							{dirty && <Chip size="small" variant="outlined" label="As last saved"/>}
						</Box>
						<Reach reach={server && server.reach} status={liveStatus}/>
					</Paper>

					<Typography variant="caption" color="text.secondary" sx={{display: 'block', mt: 2}}>
						Past announcements are kept in the history of the <code>ANNOUNCEMENT</code> setting,
						under App Config.
					</Typography>

				</Grid>
			</Grid>
		</Container>
	)
}

export default Announcements
